import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

type StateListenerType<T> = (state: T, eventMeta?: any) => void;

export class StateManager<T = any> {
  private state: T;
  private listeners: Array<StateListenerType<T>> = [];

  constructor(initState: T) {
    this.state = initState;
  }

  getState() {
    return this.state;
  }

  subscribe(listener: StateListenerType<T>) {
    this.listeners = [...this.listeners, listener];
  }

  unsubscribe(listener: StateListenerType<T>) {
    this.listeners = this.listeners.filter((l) => l !== listener);
  }

  updateState(newState: Partial<T>, eventMeta?: any) {
    this.state = { ...this.state, ...newState };
    this.listeners.forEach((l) => l(this.state, eventMeta));
  }
}

const ComponentContext = createContext({} as StateManager);

export const ComponentContextComponent = function <T>({
  children,
  stateManager,
}: {
  children: ReactNode;
  stateManager: StateManager<T>;
}) {
  return (
    <ComponentContext.Provider value={stateManager}>
      {children}
    </ComponentContext.Provider>
  );
};

export const useStateManager = <T,>(): StateManager<T> =>
  useContext(ComponentContext);

export const useComponentState = <T = any,>() => {
  const stateManager = useStateManager<T>();
  const [state, setState] = useState(stateManager.getState());
  useEffect(() => {
    const listener = (newState: T) => {
      setState(newState);
    };
    stateManager.subscribe(listener);
    return () => {
      stateManager.unsubscribe(listener);
    };
  }, []);

  return state;
};
